import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import userService, { type AvailableUser } from '@/services/user.service';
import type { RootState } from './index';

// ==============================================
// Types
// ==============================================

interface UserState {
  availableUsers: AvailableUser[];
  loading: boolean;
  error: string | null;
}

// ==============================================
// Initial State
// ==============================================

const initialState: UserState = {
  availableUsers: [],
  loading: false,
  error: null,
};

// ==============================================
// Async Thunks
// ==============================================

/**
 * Fetch all available users for expense splits
 */
export const fetchAvailableUsers = createAsyncThunk(
  'users/fetchAvailable',
  async (_, { rejectWithValue }) => {
    try {
      return await userService.getAvailableUsers();
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch available users');
    }
  }
);

// ==============================================
// Slice
// ==============================================

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch available users
      .addCase(fetchAvailableUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableUsers.fulfilled, (state, action: PayloadAction<AvailableUser[]>) => {
        state.loading = false;
        state.availableUsers = action.payload;
      })
      .addCase(fetchAvailableUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// ==============================================
// Actions & Selectors
// ==============================================

export const { clearError } = userSlice.actions;

export const selectAvailableUsers = (state: RootState) => state.users.availableUsers;
export const selectUsersLoading = (state: RootState) => state.users.loading;
export const selectUsersError = (state: RootState) => state.users.error;

export default userSlice.reducer;
